export interface SignupAttribution {
  signup_source: string;
  signup_medium: string | null;
  signup_campaign: string | null;
  signup_attribution: "utm" | "referrer" | "direct";
  ft_source: string;
  ft_medium: string | null;
  ft_campaign: string | null;
  ft_landed_at: string | null;
}

const POSTHOG_ATTRIBUTION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "fbclid",
] as const;

const POSTHOG_FIRST_TOUCH_KEYS = [
  "ft_referrer",
  "ft_landing_page",
  "ft_ts",
  "ft_utm_source",
  "ft_utm_medium",
  "ft_utm_campaign",
  "ft_utm_content",
  "ft_utm_term",
  "ft_fbclid",
] as const;

type PostHogSignupClient = {
  get_property: (key: string) => unknown;
  identify: (distinctId: string, propertiesToSet?: Record<string, unknown>) => void;
  setPersonProperties: (
    propertiesToSet?: Record<string, unknown>,
    propertiesToSetOnce?: Record<string, unknown>,
  ) => void;
};

type PostHogSignupAttributionInput = {
  getProperty: (key: string) => unknown;
  email: string;
  papel: "teacher" | "school";
  signupTs?: string;
};

type PostHogSignupIdentity = Omit<PostHogSignupAttributionInput, "getProperty"> & {
  userId: string;
};

type PostHogSignupPersonClient = Pick<
  PostHogSignupClient,
  "get_property" | "setPersonProperties"
>;

export function buildPostHogSignupAttribution({
  getProperty,
  email,
  papel,
  signupTs = new Date().toISOString(),
}: PostHogSignupAttributionInput) {
  const set: Record<string, unknown> = { email, papel };
  const setOnce: Record<string, unknown> = { signup_ts: signupTs };

  for (const key of POSTHOG_ATTRIBUTION_KEYS) {
    const lastTouchValue = getProperty(key);
    if (lastTouchValue) {
      set[key] = lastTouchValue;
    }
  }

  for (const key of POSTHOG_FIRST_TOUCH_KEYS) {
    const firstTouchValue = getProperty(key);
    if (firstTouchValue !== undefined) {
      setOnce[key] = firstTouchValue;
    }
  }

  return {
    set,
    setOnce,
    eventProperties: { ...set, ...setOnce },
  };
}

export function identifyPostHogSignup(
  client: PostHogSignupClient,
  { userId, ...identity }: PostHogSignupIdentity,
): Record<string, unknown> {
  const attribution = buildPostHogSignupAttribution({
    ...identity,
    getProperty: key => client.get_property(key),
  });
  client.identify(userId, attribution.set);
  client.setPersonProperties({}, attribution.setOnce);
  return attribution.eventProperties;
}

export function setPostHogSignupPersonProperties(
  client: PostHogSignupPersonClient,
  input: Omit<PostHogSignupAttributionInput, "getProperty">,
): Record<string, unknown> {
  const attribution = buildPostHogSignupAttribution({
    ...input,
    getProperty: key => client.get_property(key),
  });
  // O segundo argumento usa $set_once: o primeiro toque nunca é sobrescrito.
  client.setPersonProperties(attribution.set, attribution.setOnce);
  return attribution.eventProperties;
}

export async function persistPostHogSignupAttribution({
  client,
  identifyFromToken,
  token,
  ...input
}: Omit<PostHogSignupAttributionInput, "getProperty"> & {
  client: PostHogSignupPersonClient;
  identifyFromToken: (token: string) => Promise<void> | void;
  token: string;
}): Promise<Record<string, unknown>> {
  await identifyFromToken(token);
  return setPostHogSignupPersonProperties(client, input);
}

const FIRST_TOUCH_KEY = "gamefik_first_touch";

function inferMedium(domain: string): string {
  if (["google.com", "bing.com", "duckduckgo.com"].includes(domain)) return "organic";
  if (["instagram.com", "facebook.com", "linkedin.com"].includes(domain)) return "social";
  if (domain.includes("gamefik.com")) return "internal";
  return "referral";
}

function getSessionSource(): Pick<SignupAttribution, "signup_source" | "signup_medium" | "signup_campaign" | "signup_attribution"> {
  if (typeof window === "undefined") {
    return { signup_source: "direct", signup_medium: null, signup_campaign: null, signup_attribution: "direct" };
  }

  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get("utm_source");

  if (utmSource) {
    return {
      signup_source: utmSource,
      signup_medium: params.get("utm_medium"),
      signup_campaign: params.get("utm_campaign"),
      signup_attribution: "utm",
    };
  }

  const referrer = document.referrer;
  if (referrer) {
    try {
      const domain = new URL(referrer).hostname.replace("www.", "");
      return {
        signup_source: domain,
        signup_medium: inferMedium(domain),
        signup_campaign: null,
        signup_attribution: "referrer",
      };
    } catch {
      // invalid referrer URL — fall through to direct
    }
  }

  return { signup_source: "direct", signup_medium: null, signup_campaign: null, signup_attribution: "direct" };
}

function getFirstTouch(): { source?: string; medium?: string | null; campaign?: string | null; landed_at?: string } {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(FIRST_TOUCH_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/** Call on every page load (e.g. in _app.tsx) to persist the first UTM visit. */
export function saveFirstTouchIfMissing(): void {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("utm_source") && !localStorage.getItem(FIRST_TOUCH_KEY)) {
      localStorage.setItem(
        FIRST_TOUCH_KEY,
        JSON.stringify({
          source: params.get("utm_source"),
          medium: params.get("utm_medium"),
          campaign: params.get("utm_campaign"),
          landed_at: window.location.pathname,
        }),
      );
    }
  } catch {
    // ignore storage errors
  }
}

/**
 * Returns both last-touch (current session) and first-touch attribution.
 * Use this when calling posthog.identify() or posthog.capture() at signup.
 */
export function getSignupAttribution(): SignupAttribution {
  const session = getSessionSource();
  const ft = getFirstTouch();
  return {
    ...session,
    ft_source: ft.source ?? session.signup_source,
    ft_medium: ft.medium ?? session.signup_medium,
    ft_campaign: ft.campaign ?? session.signup_campaign,
    ft_landed_at: ft.landed_at ?? null,
  };
}
